/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  GamoraController,
  GamoraControllerAbortContextInput,
  GamoraControllerContextIdentifier,
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakClientStateContext,
  HoruzProtocolChulakClientStateContextRegistry,
} from './states/context.js';
import {
  HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientTransitionMap,
} from './statemap.js';
import {
  HoruzProtocolChulakErrorAppCancel,
  HoruzProtocolChulakErrorAppError,
  HoruzProtocolChulakErrorAppInvalidResponse,
  HoruzProtocolChulakErrorAppTimeout,
  HoruzProtocolChulakErrorProtocolUnknown,
} from './errors/index.js';
import { HoruzProtocolChulakClientStateAbort } from './states/client/abort.state.js';
import { HoruzProtocolChulakClientStateAgentStart } from './states/client/agent-start.state.js';
import { HoruzProtocolChulakClientStateAppPairing } from './states/client/app-pairing.state.js';
import { HoruzProtocolChulakClientStateAppPresentation } from './states/client/app-presentation.state.js';
import { HoruzProtocolChulakClientStateAuth } from './states/client/auth.state.js';
import { HoruzProtocolChulakClientStateDone } from './states/client/done.state.js';
import { HoruzProtocolChulakCloseCode } from './codes.js';
import { IHoruz } from '../../horuz.js';
const debug = debugCreate({ namespace: 'horuz:protocols:chulak:client' });

/**
 * @internal
 */
export type HoruzProtocolChulakClientControllerArgs<TRoot extends IHoruz> =
  GamoraFoundationNodeArgs<typeof HoruzProtocolChulakClientStateMap>;

/**
 * HoruzProtocolChulakClientController
 * @public
 */
export class HoruzProtocolChulakClientController<
  TRoot extends IHoruz,
> extends GamoraController<
  typeof HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientStateContext,
  TRoot
> {
  constructor(args: HoruzProtocolChulakClientControllerArgs<TRoot>) {
    assert(
      typeof args === 'object' && args !== null,
      'Invalid args argument: not an object.',
    );

    // Initialize base
    super({
      ...args,
      stateMap: {
        // Used states
        Auth: [HoruzProtocolChulakClientStateAuth, {}],
        AppPairing: [HoruzProtocolChulakClientStateAppPairing, {}],
        AppPresentation: [HoruzProtocolChulakClientStateAppPresentation, {}],
        Done: [HoruzProtocolChulakClientStateDone, {}],
        AgentStart: [HoruzProtocolChulakClientStateAgentStart, {}],
        Abort: [HoruzProtocolChulakClientStateAbort, {}],
      },
      transitionMap: HoruzProtocolChulakClientTransitionMap,
    });
  }

  /**
   * Creates context registry
   */
  createContextRegistry(): HoruzProtocolChulakClientStateContextRegistry {
    // Create context
    return {};
  }

  /**
   * Event
   *
   * Handle request
   */
  handle(ws: WebSocket): GamoraControllerContextIdentifier {
    // Create context
    const contextIdentifier = this.$createContext({
      defaultStateAbort: 'Abort',
      defaultStateStart: 'Auth',
    });

    // Create new context
    const context = this.$getContext(contextIdentifier);

    // Create context
    context.stateContext.ws = ws;
    context.stateContext.stateContextRegistry = this.createContextRegistry();

    // Set array buffer binary type
    ws.binaryType = 'arraybuffer';

    // Setup close event
    ws.onclose = (event) => {
      debug.log(
        '(handle): WebSocket close event for contextIdentifier: ' +
          contextIdentifier +
          ' with code: ' +
          event.code +
          '.',
      );

      switch (event.code) {
        case HoruzProtocolChulakCloseCode.OKAY:
        case HoruzProtocolChulakCloseCode.AGENT_CANCEL:
        case HoruzProtocolChulakCloseCode.AGENT_DISCONNECT: {
          // The context will finalize
          break;
        }
        case HoruzProtocolChulakCloseCode.APP_CANCEL: {
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAppCancel.Create('App cancelled.');

          // Try to abort context
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Abort',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to abort context contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
          break;
        }
        case HoruzProtocolChulakCloseCode.APP_TIMEOUT: {
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAppTimeout.Create('App timeout.');
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Abort',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to abort context contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
          break;
        }
        case HoruzProtocolChulakCloseCode.APP_ERROR: {
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAppError.Create('App error.');
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Abort',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to abort context contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
          break;
        }
        case HoruzProtocolChulakCloseCode.APP_INVALID_RESPONSE: {
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAppInvalidResponse.Create(
              'App invalid response.',
            );
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Abort',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to abort context contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
          break;
        }
        default: {
          if (!context.stateContext.stateContextRegistry.lastError) {
            context.stateContext.stateContextRegistry.lastError =
              HoruzProtocolChulakErrorProtocolUnknown.Create(
                `${event.reason ?? 'Closed without reason'}: ${event.code} `,
              );
          }
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Abort',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to abort context contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
        }
      }

      // Clear message queue
      this.$root.$('queues').$('data').empty(contextIdentifier);
    };

    // Setup error event
    ws.onerror = () => {
      debug.log(
        '(handle): WebSocket error event for contextIdentifier: ' +
          contextIdentifier +
          '.',
      );
    };

    // Setup close event
    ws.onmessage = (message) => {
      debug.log(
        '(handle): WebSocket message event for contextIdentifier: ' +
          contextIdentifier +
          '.',
      );
      assert(
        ws.binaryType === 'arraybuffer',
        'Unsupported binaryType in ws: ' + ws.binaryType,
      );

      // Convert to uint8 array
      const uint8Array = new Uint8Array(message.data);

      // Handle message
      const isQueued = this.$root
        .$('queues')
        .$('data')
        .queue(contextIdentifier, uint8Array);

      // Failed to process message
      if (!isQueued) {
        debug.log(
          '(handle): Unable to queue message event for contextIdentifier: ' +
            contextIdentifier +
            '.',
        );

        // Error
        context.stateContext.stateContextRegistry.lastError =
          HoruzProtocolChulakErrorProtocolUnknown.Create(
            'Unable to queue message.',
          );

        // Abort
        this.$tryAbortContext(contextIdentifier, {
          stateAbort: 'Abort',
        })?.catch((e) => {
          debug.trace(e?.message, e?.stack);
          debug.log(
            '(handle): Unable to abort context contextIdentifier: ' +
              contextIdentifier +
              '.',
          );
        });
      }
    };

    return contextIdentifier;
  }
}
